import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Lightbulb, ArrowRight, Target, CheckCircle, 
  FileText, Search, GraduationCap, MessageSquare,
  User, Briefcase, X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { progressAPI } from '@/lib/api';

// Icon mapping for different tool keys
const toolIcons = {
  profile: User,
  build_new_resume: FileText,
  ats_resume_check: CheckCircle,
  career_assessments: Target,
  search_jobs: Search,
  interview_prep: MessageSquare,
  my_learning: GraduationCap,
  job_tracker: Briefcase
};

const RecommendedAction = () => {
  const navigate = useNavigate();
  const [recommendation, setRecommendation] = useState(null);
  const [allRecommendations, setAllRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    loadRecommendation();
  }, []);

  const loadRecommendation = async () => {
    try {
      const response = await progressAPI.getRecommendedAction();
      if (response.data) {
        setRecommendation(response.data.recommended);
        setAllRecommendations(response.data.all_recommendations || []);
      }
    } catch (error) {
      console.debug('Failed to load recommendations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    // Store in sessionStorage so it comes back on page refresh
    sessionStorage.setItem('recommendedActionDismissed', 'true');
  };

  // Check if dismissed in session
  useEffect(() => {
    if (sessionStorage.getItem('recommendedActionDismissed') === 'true') {
      setDismissed(true);
    }
  }, []);

  if (loading || dismissed || !recommendation) {
    return null;
  }

  const Icon = toolIcons[recommendation.tool_key] || Lightbulb;

  return (
    <div className="mb-8">
      {/* Main Recommendation Card */}
      <div className="relative overflow-hidden bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 rounded-2xl p-6 text-white shadow-lg">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full -translate-y-32 translate-x-32" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white rounded-full translate-y-24 -translate-x-24" />
        </div>
        
        {/* Dismiss Button */}
        <button 
          onClick={handleDismiss}
          className="absolute top-4 right-4 p-1 hover:bg-white/20 rounded-full transition-colors"
          title="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
        
        {/* Content */}
        <div className="relative flex items-start gap-4">
          <div className="flex-shrink-0 w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
            <Lightbulb className="w-7 h-7" />
          </div>
          
          <div className="flex-1 min-w-0">
            <p className="text-blue-100 text-sm font-medium mb-1">Recommended Next Step</p>
            <h3 className="text-xl font-bold mb-2">{recommendation.action}</h3>
            <p className="text-blue-100 text-sm mb-4">{recommendation.description}</p>
            
            <Button 
              onClick={() => navigate(recommendation.path)}
              className="bg-white text-indigo-600 hover:bg-blue-50 shadow-md"
            >
              <Icon className="w-4 h-4 mr-2" />
              Get Started
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
        
        {/* Other Recommendations Preview */}
        {allRecommendations.length > 1 && (
          <div className="relative mt-6 pt-4 border-t border-white/20">
            <p className="text-xs text-blue-200 mb-3">Also recommended:</p>
            <div className="flex flex-wrap gap-2">
              {allRecommendations.slice(1, 3).map((rec, idx) => {
                const RecIcon = toolIcons[rec.tool_key] || Target;
                return (
                  <button
                    key={idx}
                    onClick={() => navigate(rec.path)}
                    className="flex items-center gap-2 px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-lg text-sm transition-colors"
                  >
                    <RecIcon className="w-3.5 h-3.5" />
                    {rec.action}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RecommendedAction;
